import _extends from "@babel/runtime/helpers/extends";
import { Box, Divider } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { getManualFixSurfaceProps, RuleFooter } from '@wix/rules-panel-ui';
import { useSetAtom } from 'jotai/index';
import React from 'react';
import { colorsInSessionAtom } from '../../../../../atoms/session-atoms';
import { useA11yWizardAdapter } from '../../../../../hooks/use-a11y-wizard-adapter';
import { useResetUnsavedChangesHandlers } from '../../../../../hooks/use-reset-unsaved-changes-handlers';
import { getFixerDesignPropsFromCapabilities } from '../../../../../utils/get-fixer-design-props';
import { useCurrentElement } from '../../hooks/use-current-element';
import { ContrastRatioLevel } from '../../types';
import { ColorSlider } from './color-slider';
import { RatioDisplay } from './ratio-display';
const BRIGHTNESS_MAX_SCALE = 100;
export const ColorContrastControls = _ref => {
  var _designProps$ruleFoot;
  let {
    failedElement,
    onClickCancel,
    onClickApply,
    onChangeElementsColor,
    showLoader,
    elementIndex,
    status,
    isGeneric,
    pageId
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    minimalRatio
  } = failedElement;
  const {
    capabilities
  } = useA11yWizardAdapter();
  const designProps = getFixerDesignPropsFromCapabilities(capabilities);
  const softIssueListSurfaces = capabilities.wizardPresentation.softIssueListSurfaces;
  const manualFixSurfaceProps = getManualFixSurfaceProps(capabilities.wizardPresentation);
  const {
    bgColorState,
    textColorState,
    currentContrastRatio,
    hasColorsChanged,
    isApplyClickedRef,
    updateUserColorPalette,
    updatedElement
  } = useCurrentElement({
    element: failedElement,
    elementIndex,
    isGeneric,
    minimalRatio,
    onChangeElementsColor,
    onClickApply,
    pageId
  });
  const setColorsInSession = useSetAtom(colorsInSessionAtom);
  const {
    resetUnsavedChangesHandlers
  } = useResetUnsavedChangesHandlers();

  // TODO: once we implement the AAA level, we should update this condition
  const currentContrastRatioLevel = currentContrastRatio >= minimalRatio ? ContrastRatioLevel.AA : ContrastRatioLevel.Fail;
  const sliderAndRatioContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ColorSlider, {
    colorPickerAriaLabel: t('accessibility-wizard.rules-list.color-contrast.color-picker.foreground.aria-label', {
      color: textColorState.hex
    }),
    colorState: textColorState,
    currentContrastRatioLevel: currentContrastRatioLevel,
    index: elementIndex,
    isGeneric: isGeneric,
    label: t('accessibility-wizard.rules-list.color-contrast.element.foreground'),
    maxScale: BRIGHTNESS_MAX_SCALE,
    sliderType: "foreground",
    status: status
  }), /*#__PURE__*/React.createElement(ColorSlider, {
    colorPickerAriaLabel: t('accessibility-wizard.rules-list.color-contrast.color-picker.background.aria-label', {
      color: bgColorState.hex
    }),
    colorState: bgColorState,
    currentContrastRatioLevel: currentContrastRatioLevel,
    index: elementIndex,
    isGeneric: isGeneric,
    label: t('accessibility-wizard.rules-list.color-contrast.element.background'),
    maxScale: BRIGHTNESS_MAX_SCALE,
    sliderType: "background",
    status: status
  }), !isGeneric && /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(RatioDisplay, {
    contrastRatioLevel: currentContrastRatioLevel,
    index: elementIndex,
    isGeneric: isGeneric,
    ratio: currentContrastRatio,
    status: status
  }));
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: designProps.formGap ?? 'SP1'
  }, softIssueListSurfaces ? /*#__PURE__*/React.createElement(Box, _extends({
    borderRadius: "6px",
    direction: "vertical",
    gap: designProps.formGap ?? 'SP1',
    padding: "SP2"
  }, manualFixSurfaceProps), sliderAndRatioContent) : sliderAndRatioContent, !isGeneric && /*#__PURE__*/React.createElement(RuleFooter, {
    design: {
      buttonSize: designProps == null || (_designProps$ruleFoot = designProps.ruleFooter) == null ? void 0 : _designProps$ruleFoot.buttonSize
    },
    dismissButtonProps: {
      label: t('accessibility-wizard.rules-list.color-contrast.cta.secondary'),
      onClick: () => {
        onClickCancel();
        resetUnsavedChangesHandlers();
      },
      type: 'button'
    },
    submitButtonProps: {
      disabled: currentContrastRatioLevel === ContrastRatioLevel.Fail || !hasColorsChanged,
      label: t('accessibility-wizard.rules-list.color-contrast.cta.primary'),
      onClick: () => {
        setColorsInSession(updateUserColorPalette);
        onClickApply({
          bgColorState,
          currentContrastRatio,
          currentContrastRatioLevel,
          element: updatedElement,
          elementIndex,
          textColorState
        });
        isApplyClickedRef.current = true;
        resetUnsavedChangesHandlers();
      },
      showLoader
    }
  }));
};
//# sourceMappingURL=color-contrast-controls.js.map